import snakecaseKeys from 'snakecase-keys';
import { GoogleProtobufAny, CosmosBaseV1Beta1Coin, CosmosFeegrantV1Beta1Tx, GoogleProtobufTimestamp, CosmosFeegrantV1Beta1Feegrant, } from '@injectivelabs/core-proto-ts';
import { MsgBase } from '../../MsgBase.js';
const basicAllowanceType = '/cosmos.feegrant.v1beta1.BasicAllowance';
/**
 * @category Messages
 */
export default class MsgGrantAllowance extends MsgBase {
    static fromJSON(params) {
        return new MsgGrantAllowance(params);
    }
    toProto() {
        const { params } = this;
        const timestamp = this.getTimestamp();
        const basicAllowance = CosmosFeegrantV1Beta1Feegrant.BasicAllowance.create();
        basicAllowance.spendLimit = params.allowance.spendLimit.map(({ denom, amount }) => {
            const coin = CosmosBaseV1Beta1Coin.Coin.create();
            coin.denom = denom;
            coin.amount = amount;
            return coin;
        });
        basicAllowance.expiration = new Date(Number(timestamp.seconds) * 1000);
        const allowance = GoogleProtobufAny.Any.create();
        allowance.typeUrl = basicAllowanceType;
        allowance.value = Buffer.from(CosmosFeegrantV1Beta1Feegrant.BasicAllowance.encode(basicAllowance).finish());
        const message = CosmosFeegrantV1Beta1Tx.MsgGrantAllowance.create();
        message.granter = params.granter;
        message.grantee = params.grantee;
        message.allowance = allowance;
        return CosmosFeegrantV1Beta1Tx.MsgGrantAllowance.fromJSON(message);
    }
    toData() {
        const proto = this.toProto();
        return {
            '@type': '/cosmos.feegrant.v1beta1.MsgGrantAllowance',
            ...proto,
        };
    }
    toAmino() {
        const { params } = this;
        const proto = this.toProto();
        const timestamp = this.getTimestamp();
        const message = proto;
        const messageWithAllowance = snakecaseKeys({
            ...message,
            allowance: {
                type: 'cosmos-sdk/BasicAllowance',
                value: {
                    spendLimit: params.allowance.spendLimit.map(({ denom, amount }) => ({
                        denom,
                        amount,
                    })),
                    expiration: new Date(Number(timestamp.seconds) * 1000)
                        .toISOString()
                        .replace('.000Z', 'Z'),
                },
            },
        });
        return {
            type: 'cosmos-sdk/MsgGrantAllowance',
            value: messageWithAllowance,
        };
    }
    toDirectSign() {
        const proto = this.toProto();
        return {
            type: '/cosmos.feegrant.v1beta1.MsgGrantAllowance',
            message: proto,
        };
    }
    toWeb3Gw() {
        const { params } = this;
        const amino = this.toAmino();
        const timestamp = this.getTimestamp();
        const messageWithAllowance = {
            granter: amino.value.granter,
            grantee: amino.value.grantee,
            allowance: {
                '@type': basicAllowanceType,
                spendLimit: params.allowance.spendLimit.map(({ denom, amount }) => ({
                    denom,
                    amount,
                })),
                expiration: new Date(Number(timestamp.seconds) * 1000)
                    .toISOString()
                    .replace('.000Z', 'Z'),
            },
        };
        return {
            '@type': '/cosmos.feegrant.v1beta1.MsgGrantAllowance',
            ...messageWithAllowance,
        };
    }
    toEip712V2() {
        const web3Gw = this.toWeb3Gw();
        const messageAdjustedForEip712V2 = {
            ...web3Gw,
            allowance: {
                '@type': web3Gw.allowance['@type'],
                spend_limit: web3Gw.allowance.spendLimit,
                expiration: web3Gw.allowance.expiration,
            },
        };
        return messageAdjustedForEip712V2;
    }
    getTimestamp() {
        const { params } = this;
        if (params.allowance.expiration) {
            const timestamp = GoogleProtobufTimestamp.Timestamp.create();
            timestamp.seconds = params.allowance.expiration.toString();
            return timestamp;
        }
        const defaultExpiryYears = 5;
        const dateNow = new Date();
        const expiration = new Date(dateNow.getFullYear() + defaultExpiryYears, dateNow.getMonth(), dateNow.getDate());
        const timestamp = GoogleProtobufTimestamp.Timestamp.create();
        const timestampInSeconds = (expiration.getTime() / 1000).toString();
        timestamp.seconds = timestampInSeconds;
        return timestamp;
    }
    toBinary() {
        return CosmosFeegrantV1Beta1Tx.MsgGrantAllowance.encode(this.toProto()).finish();
    }
}
